import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, Check, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Carte de configuration d'une campagne Relance ────────────────────────────

function ConfigCard({ cfg, onSaved }) {
  const hasConfig = cfg.delai_carence_jours != null;

  const [form, setForm] = useState({
    delai_carence_jours:       cfg.delai_carence_jours      ?? 3,
    intervalle_relance_jours:  cfg.intervalle_relance_jours  ?? 15,
    exclure_credit_non_lettre: cfg.exclure_credit_non_lettre ?? false,
    exclure_solde_crediteur:   cfg.exclure_solde_crediteur   ?? true,
    exclure_litiges:           cfg.exclure_litiges           ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const valid = Number.isInteger(form.delai_carence_jours) && form.delai_carence_jours >= 0
             && Number.isInteger(form.intervalle_relance_jours) && form.intervalle_relance_jours > 0;

  const dirty = JSON.stringify(form) !== JSON.stringify({
    delai_carence_jours:       cfg.delai_carence_jours      ?? 3,
    intervalle_relance_jours:  cfg.intervalle_relance_jours  ?? 15,
    exclure_credit_non_lettre: cfg.exclure_credit_non_lettre ?? false,
    exclure_solde_crediteur:   cfg.exclure_solde_crediteur   ?? true,
    exclure_litiges:           cfg.exclure_litiges           ?? true,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateRelanceConfigByCampaign(cfg.campaign_id, form);
      toast.success(`Config enregistrée pour ${cfg.campaign_label || cfg.campaign_id}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border p-5 space-y-4"
         data-testid={`rc-card-${cfg.campaign_id}`}
         style={{ borderColor: 'var(--border)', background: 'white' }}>

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>
              {cfg.campaign_label || cfg.campaign_id}
            </h3>
            {cfg.societe_nom && (
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {cfg.societe_nom}
              </p>
            )}
          </div>
          {!cfg.actif && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99,
              background: '#F1F5F9', color: '#64748B', letterSpacing: '0.04em',
            }}>INACTIF</span>
          )}
        </div>
        {cfg.updated_at && (
          <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
            Modifié le {new Date(cfg.updated_at).toLocaleDateString('fr-FR')}
            {cfg.updated_by ? ` par ${cfg.updated_by}` : ''}
          </span>
        )}
      </div>

      {/* Intervalles */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
            Délai de carence (jours après échéance)
          </label>
          <input type="number" min="0" max="365"
                 value={form.delai_carence_jours}
                 onChange={e => set('delai_carence_jours', parseInt(e.target.value, 10))}
                 data-testid={`rc-carence-${cfg.campaign_id}`}
                 className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none"
                 style={{ borderColor: 'var(--border)' }} />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
            Intervalle entre relances (jours)
          </label>
          <input type="number" min="1" max="365"
                 value={form.intervalle_relance_jours}
                 onChange={e => set('intervalle_relance_jours', parseInt(e.target.value, 10))}
                 data-testid={`rc-intervalle-${cfg.campaign_id}`}
                 className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none"
                 style={{ borderColor: 'var(--border)' }} />
        </div>
      </div>

      {/* Exclusions */}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox"
                 checked={!!form.exclure_litiges}
                 onChange={e => set('exclure_litiges', e.target.checked)}
                 data-testid={`rc-excl-litiges-${cfg.campaign_id}`}
                 className="w-4 h-4 rounded cursor-pointer" />
          <span className="text-sm" style={{ color: 'var(--text)' }}>Exclure les litiges</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox"
                 checked={!!form.exclure_solde_crediteur}
                 onChange={e => set('exclure_solde_crediteur', e.target.checked)}
                 data-testid={`rc-excl-solde-${cfg.campaign_id}`}
                 className="w-4 h-4 rounded cursor-pointer" />
          <span className="text-sm" style={{ color: 'var(--text)' }}>Exclure les soldes créditeurs</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox"
                 checked={!!form.exclure_credit_non_lettre}
                 onChange={e => set('exclure_credit_non_lettre', e.target.checked)}
                 data-testid={`rc-excl-credit-${cfg.campaign_id}`}
                 className="w-4 h-4 rounded cursor-pointer" />
          <span className="text-sm" style={{ color: 'var(--text)' }}>Exclure au crédit non lettré</span>
        </label>
      </div>

      <div className="flex justify-end pt-1">
        <button type="button" onClick={handleSave} disabled={!valid || !dirty || saving}
                data-testid={`rc-save-${cfg.campaign_id}`}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: 'var(--primary-gradient)' }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RelancesConfig() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]    = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getRelancesConfigCampaigns();
      setCampaigns(data || []);
    } catch {
      toast.error('Impossible de charger la configuration des relances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl space-y-5" data-testid="relances-config-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>
            <BellRing size={17} />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Relances clients</h2>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Politique de recouvrement par campagne — carence, intervalle et exclusions.
              La configuration est créée automatiquement lors de la création d'une campagne Relance.
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
                data-testid="rc-refresh"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {loading && campaigns.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Aucune campagne Relance — créez une campagne de type "Relance" depuis la page Campagnes.
          </p>
        </div>
      ) : (
        campaigns.map(cfg => (
          <ConfigCard key={cfg.campaign_id} cfg={cfg} onSaved={load} />
        ))
      )}
    </div>
  );
}
